import { PayloadAction } from '@reduxjs/toolkit';
import { Decimal } from '@snap-types/types/decimal';
import { WritableDraft } from 'immer';
import { z } from 'zod/v4';

import { QuoteBuilderStateType } from '../../types/state';
import { EditableQuoteBuilderOperationField } from '../../types/state/operation';
import { onQuantityChange } from '../calculations/recalculate';
import { buildElementId } from '../utilities/element-id';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';

export type OperationValueChangedParams = {
  quoteLineUuid: string;
  snapOperationUuid: string;
  field: EditableQuoteBuilderOperationField;
  value: string;
};

const validator = z.coerce
  .number({
    error: 'Operation value is required',
  })
  .positive({ message: 'Operation value must be greater than 0' });

export function operationValueChanged(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<OperationValueChangedParams>,
) {
  const { value, quoteLineUuid, snapOperationUuid, field } = action.payload;

  const result = validator.safeParse(value);
  const invalidIssueId = buildIssueId({
    area: 'snapOperation',
    quoteLineUuid,
    snapOperationUuid,
    field,
    type: 'invalid',
  });

  if (!result.success) {
    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid Field',
      description: z.flattenError(result.error).formErrors[0],
      severity: 'error',
      area: 'snapOperation',
      quoteLineUuid,
      snapOperationUuid,
      type: 'invalid',
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: snapOperationUuid,
          field,
        }),
      },
    });
  }

  deleteIssue(state, invalidIssueId);

  const decimalValue = new Decimal(result.data).toDecimalPlaces(
    state.calculationConfig.decimalPlaces,
  );

  const snapWorkOrder = state.quoteLines[quoteLineUuid].snapWorkOrder;

  if (!snapWorkOrder) {
    return;
  }

  snapWorkOrder.snapOperations[snapOperationUuid][field] = decimalValue;

  for (const quotePriceUuid of state.quoteLines[quoteLineUuid]
    .quotePriceUuids) {
    onQuantityChange(state, {
      quoteLineUuid,
      quotePriceUuid,
      quantity:
        state.quoteLines[quoteLineUuid].quotePrices[quotePriceUuid].quantity,
    });
  }
}
